import {
  CloseRounded,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  SlideshowOutlined,
  TableChartOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  Typography,
} from '@mui/material';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { COLORS } from '../../ReportingV2/theme';

export type DashboardExportFormat = 'csv' | 'excel' | 'pdf' | 'pptx';

const FORMATS: Array<{ value: DashboardExportFormat; label: string; hint: string; icon: ReactElement }> = [
  {
    value: 'csv',
    label: 'CSV',
    hint: 'One file, all sections stacked',
    icon: <TableChartOutlined sx={{ fontSize: 18 }} />,
  },
  {
    value: 'excel',
    label: 'Excel',
    hint: 'One sheet per section, sized columns',
    icon: <DescriptionOutlined sx={{ fontSize: 18 }} />,
  },
  {
    value: 'pdf',
    label: 'PDF',
    hint: 'Snapshot of the page as you see it',
    icon: <PictureAsPdfOutlined sx={{ fontSize: 18 }} />,
  },
  {
    value: 'pptx',
    label: 'PowerPoint',
    hint: 'One slide per widget',
    icon: <SlideshowOutlined sx={{ fontSize: 18 }} />,
  },
];

/**
 * One Export dialog replacing the three separate "Export PPTX"/"Export CSV"/
 * "Export Excel" buttons (flank#5061), mirroring Unified Reporting's own
 * ExportDialog.tsx: format choice, then "Current view" (every widget on the
 * page) or "Choose widgets" (tick which ones go in). PDF is `window.print()`
 * through this page's own `@media print` sx rules (DashboardBuilder.tsx),
 * same mechanism Unified Reporting's PDF uses, not a separate generator.
 *
 * Unlike Unified Reporting's PDF, this one always exports "Current view" —
 * Unified Reporting supports a per-section PDF by toggling a CSS class on
 * each section's DOM node before printing; this page has no equivalent
 * per-widget hook to hide one widget from the print output, and guessing one
 * without a way to visually check real print output in this session risks a
 * worse bug (a widget silently missing from a client's PDF) than the smaller
 * scope. "Choose widgets" is disabled for this format accordingly.
 */
const DashboardExportDialog = ({
  open,
  onClose,
  widgets,
  exporting,
  disabledReason,
  csvExcelDisabledReason,
  onExport,
}: {
  open: boolean;
  onClose: () => void;
  widgets: Array<{ id: string; title: string }>;
  exporting: boolean;
  /** Blocks every format — a widget is still loading, or one failed. */
  disabledReason: string;
  /** Blocks CSV/Excel only — PowerPoint can still export a chart-only page these can't. */
  csvExcelDisabledReason: string;
  onExport: (format: DashboardExportFormat, widgetIds: string[] | null) => void;
}) => {
  const [format, setFormat] = useState<DashboardExportFormat>('csv');
  const [chooseWidgets, setChooseWidgets] = useState(false);
  const [selected, setSelected] = useState<string[]>(widgets.map(w => w.id));

  // PDF always exports "Current view" (see the component doc comment) — drop
  // any prior "Choose widgets" selection if the viewer switches to PDF after
  // making one, rather than silently ignoring it on export.
  useEffect(() => {
    if (format === 'pdf') setChooseWidgets(false);
  }, [format]);

  // This dialog stays mounted across opens/closes (MUI's own `open` prop
  // convention), so `selected`'s initial state only ever ran once — if the
  // viewer changed pages while the dialog was closed, reopening it kept the
  // PREVIOUS page's widget IDs selected (CodeRabbit, flank#5065). Reset only
  // on open, not on every `widgets` reference change, so a page's widget list
  // updating while the dialog is open doesn't discard an in-progress pick.
  useEffect(() => {
    if (open) setSelected(widgets.map(w => w.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (id: string) => setSelected(prev => (prev.includes(id) ? prev.filter(w => w !== id) : [...prev, id]));

  const formatReason = format === 'csv' || format === 'excel' ? csvExcelDisabledReason : '';
  const reason = disabledReason || formatReason;
  // Against the CURRENTLY RENDERED widgets, not raw selected.length — a stale
  // `selected` entry for a widget removed from the page would otherwise read
  // as "something's chosen" when nothing exportable actually is.
  const nothingChosen = chooseWidgets && !widgets.some(w => selected.includes(w.id));

  const handleExport = () => {
    onExport(format, chooseWidgets ? selected : null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pb: 0.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant='h6'>Export report</Typography>
          <Typography variant='body2' sx={{ color: COLORS.muted, mt: 0.5 }}>
            Exports match what's on screen — current date range, filters and widgets.
          </Typography>
        </Box>
        <IconButton size='small' onClick={onClose} aria-label='Close export dialog'>
          <CloseRounded fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant='caption' sx={{ color: COLORS.muted, fontWeight: 600, letterSpacing: 0.6 }}>
          FORMAT
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1, mb: 2.5 }}>
          {FORMATS.map(f => (
            <Box
              key={f.value}
              role='radio'
              aria-checked={format === f.value}
              tabIndex={0}
              onClick={() => setFormat(f.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setFormat(f.value);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.25,
                border: 1,
                borderRadius: 1.5,
                cursor: 'pointer',
                borderColor: format === f.value ? COLORS.primary : 'divider',
                bgcolor: format === f.value ? COLORS.pageBg : 'transparent',
              }}
            >
              <Radio checked={format === f.value} size='small' sx={{ p: 0 }} />
              {f.icon}
              <Box>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {f.label}
                </Typography>
                <Typography variant='caption' sx={{ color: COLORS.muted }}>
                  {f.hint}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography variant='caption' sx={{ color: COLORS.muted, fontWeight: 600, letterSpacing: 0.6 }}>
          SCOPE
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 0.5, alignItems: 'center' }}>
          <FormControlLabel
            control={<Radio size='small' checked={!chooseWidgets} onChange={() => setChooseWidgets(false)} />}
            label={<Typography variant='body2'>Current view</Typography>}
          />
          <FormControlLabel
            disabled={format === 'pdf'}
            control={<Radio size='small' checked={chooseWidgets} onChange={() => setChooseWidgets(true)} />}
            label={<Typography variant='body2'>Choose widgets</Typography>}
          />
        </Box>
        {format === 'pdf' && (
          <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block', mt: 0.5 }}>
            PDF always captures the current view.
          </Typography>
        )}

        {chooseWidgets && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {widgets.map(w => (
              <FormControlLabel
                key={w.id}
                sx={{ display: 'flex' }}
                control={<Checkbox size='small' checked={selected.includes(w.id)} onChange={() => toggle(w.id)} />}
                label={<Typography variant='body2'>{w.title}</Typography>}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        {nothingChosen && (
          <Typography variant='caption' sx={{ color: COLORS.red, textAlign: 'right' }}>
            Select at least one widget to export.
          </Typography>
        )}
        {!nothingChosen && reason && (
          <Typography variant='caption' sx={{ color: COLORS.muted, textAlign: 'right' }}>
            {reason}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            disableElevation
            disabled={nothingChosen || Boolean(reason) || exporting}
            onClick={handleExport}
            sx={{ textTransform: 'none', bgcolor: COLORS.black, '&:hover': { bgcolor: '#000' } }}
          >
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardExportDialog;
